import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Eye, ChevronDown, X } from "lucide-react";

export const STAGES = [
  "Reading your CV", "Identifying experience", "Extracting skills",
  "Understanding projects", "Mapping certifications", "Building your career profile",
];

export const confMeta = (v) => {
  if (v == null) return null;
  if (v >= 85) return { cls: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10", label: "High confidence" };
  if (v >= 60) return { cls: "text-amber-300 border-amber-400/40 bg-amber-500/10", label: "Review suggested" };
  return { cls: "text-rose-300 border-rose-400/40 bg-rose-500/10", label: "Needs review" };
};

export const strengthPct = (p) => {
  if (!p) return 0;
  const checks = [
    p.contact?.name, p.headline, p.summary, (p.skills || []).length >= 4,
    (p.experience || []).length, (p.projects || []).length, (p.certifications || []).length,
    (p.education || []).length, (p.skill_categories || []).length, p.recruitment_intelligence?.primary_profession,
    p.total_years != null || p.recruitment_intelligence?.total_experience,
    (p.languages || []).length,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
};

export const profCls = (v) => {
  const s = (v || "").toLowerCase();
  if (s.startsWith("expert")) return "bg-emerald-500/20 text-emerald-200 border-emerald-400/40";
  if (s.startsWith("strong")) return "bg-teal-500/20 text-teal-200 border-teal-400/40";
  if (s.startsWith("work")) return "bg-sky-500/15 text-sky-200 border-sky-400/30";
  return "bg-muted text-muted-foreground border-border";
};

export const lines = (v) => (Array.isArray(v) ? v.join("\n") : "");
export const toLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export const ConfBadge = ({ v }) => {
  const m = confMeta(v);
  if (!m) return null;
  return <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>;
};

export const Evidence = ({ text }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <span className="inline-block">
      <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-[11px] text-primary/80 hover:text-primary">
        <Eye className="h-3 w-3" /> {open ? "Hide" : "View"} CV evidence
      </button>
      {open && <span className="block mt-1 text-[11px] italic text-muted-foreground border-l-2 border-primary/40 pl-2">“{text}”</span>}
    </span>
  );
};

export const Chip = ({ children, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium bg-primary/10 border border-primary/20 text-foreground/90">
    {children}
    {onRemove && <button onClick={onRemove} className="text-muted-foreground hover:text-rose-400"><X className="h-3 w-3" /></button>}
  </span>
);

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-2xl border border-border bg-card p-5 ${className}`} {...rest}>{children}</div>
);

export const SecTitle = ({ icon: Icon, children, conf, right }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
      {Icon && <Icon className="h-3.5 w-3.5 text-primary" />} {children}
    </h3>
    <div className="flex items-center gap-2">{conf != null && <ConfBadge v={conf} />}{right}</div>
  </div>
);

export const Row = ({ l, v }) => v ? <div className="flex justify-between gap-3"><span className="text-muted-foreground">{l}</span><span className="font-medium text-right">{v}</span></div> : null;

export const MetaRow = ({ label, items }) => (
  <div className="text-xs"><span className="text-muted-foreground">{label}: </span><span className="text-foreground/80">{items.join(", ")}</span></div>
);

export const Suggestion = ({ label, value, sub, onApply }) => (
  <div className="rounded-lg border border-border p-3">
    <div className="flex items-center justify-between gap-2 mb-1"><span className="text-[10px] uppercase tracking-wider text-primary">{label}</span>{onApply && <Button size="sm" variant="outline" className="h-6 text-xs" onClick={onApply} data-testid="improve-apply">Apply</Button>}</div>
    {sub && <div className="text-xs text-muted-foreground line-through mb-1">{sub}</div>}
    <div className="text-sm text-foreground/90">{value}</div>
  </div>
);

const Block = ({ title, items, dot = "primary" }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{title}</div>
    <ul className="space-y-1.5">{items.map((h, i) => <li key={i} className="flex gap-2 text-[13px] text-foreground/75"><span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${dot === "emerald" ? "bg-emerald-400" : "bg-primary"}`} />{h}</li>)}</ul>
  </div>
);

export function ExpCard({ e }) {
  const [open, setOpen] = useState(false);
  const has = (e.responsibilities || []).length || (e.achievements || []).length || (e.technologies || []).length || (e.highlights || []).length;
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-semibold text-[15px]">{e.title}{e.company ? ` · ${e.company}` : ""}</div>
        <div className="text-[11px] font-mono text-muted-foreground shrink-0">{e.period || [e.start_date, e.end_date].filter(Boolean).join(" – ")}</div>
      </div>
      <div className="text-xs text-muted-foreground mt-0.5">{[e.location, e.duration].filter(Boolean).join(" · ")}</div>
      {e.summary && <p className="text-[13px] text-foreground/70 mt-1.5">{e.summary}</p>}
      {has ? (
        <>
          <button onClick={() => setOpen((o) => !o)} className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"><ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Hide details" : "Show details"}</button>
          {open && (
            <div className="mt-3 space-y-3">
              {(e.responsibilities || []).length > 0 && <Block title="Responsibilities" items={e.responsibilities} />}
              {(e.achievements || []).length > 0 && <Block title="Achievements" items={e.achievements} dot="emerald" />}
              {(!(e.responsibilities || []).length && !(e.achievements || []).length && (e.highlights || []).length > 0) && <Block title="Highlights" items={e.highlights} />}
              {(e.technologies || []).length > 0 && <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Technologies</div><div className="flex flex-wrap gap-1.5">{e.technologies.map((t, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>)}</div></div>}
            </div>
          )}
        </>
      ) : null}
    </Card>
  );
}
